import { useState } from "react";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

interface CommentListProps {
  token: string;
  pilId: string;
  clientId: string;
  readOnly?: boolean;
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function CommentList({
  token,
  pilId,
  clientId,
  readOnly,
}: CommentListProps) {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.cityReport.listComments.useQuery(
    { token, pilId },
    { enabled: !!token && !!pilId },
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const invalidate = () => {
    utils.cityReport.listComments.invalidate({ token, pilId });
    utils.cityReport.getReport.invalidate({ token });
  };

  const updateMut = trpc.cityReport.updateComment.useMutation({
    onSuccess: () => {
      setEditingId(null);
      setDraft("");
      invalidate();
    },
  });
  const deleteMut = trpc.cityReport.deleteComment.useMutation({
    onSuccess: invalidate,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const comments = data ?? [];

  if (comments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No comments yet. Be the first to leave feedback.
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {comments.map((c) => {
        const mine = c.client_id === clientId;
        const isEditing = editingId === c.id;
        return (
          <li
            key={c.id}
            className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2 dark:border-amber-900/40 dark:bg-amber-950/30"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{formatTs(c.created_at)}</span>
              {c.updated_at && c.updated_at !== c.created_at && (
                <span>(edited)</span>
              )}
              {mine && (
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-foreground/70">
                  You
                </span>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  disabled={updateMut.isPending}
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      updateMut.mutate({
                        token,
                        feedbackId: c.id,
                        comment: draft.trim(),
                        clientId,
                      })
                    }
                    disabled={
                      updateMut.isPending || draft.trim().length === 0
                    }
                  >
                    <Check className="size-4" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingId(null);
                      setDraft("");
                    }}
                    disabled={updateMut.isPending}
                  >
                    <X className="size-4" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm whitespace-pre-wrap">{c.comment}</p>
                {mine && !readOnly && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(c.id);
                        setDraft(c.comment);
                      }}
                    >
                      <Pencil className="size-3.5" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (
                          window.confirm("Delete this comment?")
                        ) {
                          deleteMut.mutate({
                            token,
                            feedbackId: c.id,
                            clientId,
                          });
                        }
                      }}
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 className="size-3.5" />
                      Delete
                    </Button>
                  </div>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
